import {KeyFrame, Value} from 'common/src/modules/movie/moviePrimitives.types';
import BezierCurve from 'common/src/modules/movie/utils/bezierCurve';
import {isSimpleValue} from 'common/src/modules/movie/utils/isSimpleValue';
import {animationFunctions} from 'common/src/modules/movieEditor/movieEditor.consts';
import {valueEpsilon} from 'common/src/modules/movie/movie.consts';
import {PointArr} from 'common/src/modules/movieEditor/movieEditor.types';

// this method used to calculate the value change [0-1] between the start value and end value according to the temporal bezier
// you provide time t [0-1] and return back t' [0-1] after temporal interpolation using newton raphson algo
export function getActualPercentComplete(
  idx: number, // the dimenstion to return (if the value has multiple dimensions)
  keyframe: KeyFrame<unknown>,
  percentComplete: number,
) {
  if (!keyframe.i)
    keyframe.i = {x: [animationFunctions.linear.i.x], y: [animationFunctions.linear.i.y]};
  if (!keyframe.o)
    keyframe.o = {x: [animationFunctions.linear.o.x], y: [animationFunctions.linear.o.y]};

  // the length of keyframe.i/o might less then the length of keyframe.s
  //(this is true sometimes for position property where the values for temporal interpolation should be the same for all the diemnsions, so they hold only 1 dimension)
  const idxI = Math.min(idx, keyframe.i.x.length - 1);
  const idxO = Math.min(idx, keyframe.o.x.length - 1);

  const cp1: PointArr = [keyframe.o.x[idxO], keyframe.o.y[idxO]]; // control point 1
  const cp2: PointArr = [keyframe.i.x[idxI], keyframe.i.y[idxI]]; // control point 2
  const spatialExist = keyframe?.ti || keyframe?.to;

  if (spatialExist) {
    // dont let the y value exceed 1 (similar to how after affects behave)
    // in reality this is true for position property even if the keyframes has no ti/to, but we dont have here the type of property injected
    if (cp1[1] > 1) cp1[1] = 1;
    if (cp2[1] > 1) cp2[1] = 1;
  }

  const bezier = new BezierCurve([0, 0], cp1, cp2, [1, 1]);
  let t = percentComplete;

  // newton raphson method
  // https://www.sciencedirect.com/topics/mathematics/newton-raphson-method#:~:text=The%20Newton%2DRaphson%20method%20begins,0%20crosses%20the%20x%2Daxis.
  for (let i = 0; i < 10; i++) {
    const slope = bezier.getSlopeAtTime(t, 0);
    if (slope === 0) break;
    const a = bezier.getValueAtTime(t, 0) - bezier.getSlopeAtTime(t, 0) * t;
    // a * slope*x = percentComplete => x = (percentComplete - a) / slope
    t = (percentComplete - a) / bezier.getSlopeAtTime(t, 0);
  }

  return bezier.getValueAtTime(t, 1);
}

function getMovieValue<G extends number[]>(
  value: Value<G>,
  time: number,
): G extends [number] ? number : G {
  const toClientValue = (x: number[]) => {
    x = x.map((x) => x * value.mult);

    return (x.length === 1 ? x[0] : x) as G extends [number] ? number : G;
  };

  if (isSimpleValue(value)) return toClientValue(value.val);
  if (value.k.length === 1) return toClientValue(value.k[0].s);

  for (let i = 0; i < value.k.length; i++) {
    const keyframe = value.k[i];

    if (time / 1000 >= keyframe.t) continue;

    const prevKeyframe = value.k[i - 1];
    if (!prevKeyframe) return toClientValue(keyframe.s);
    if (prevKeyframe.h) return toClientValue(prevKeyframe.s);

    const spatialExist = prevKeyframe?.ti || prevKeyframe?.to;

    // the below percentComplete is actualy the normalized t [0-1]
    const percentComplete = (time / 1000 - prevKeyframe.t) / (keyframe.t - prevKeyframe.t);

    if (!spatialExist) {
      const linearVal = prevKeyframe.s.map(
        (val, idx) =>
          val +
          (prevKeyframe.e[idx] + valueEpsilon - val) *
            getActualPercentComplete(idx, prevKeyframe, percentComplete), // getActualPercentComplete() is actually retreives temporal interpolation result [0-1] (t')
      );

      return toClientValue(linearVal);
    }

    // from now on...apply the spatial interpolation using the ti/to spatial control points
    // note that the spatial ti/to will only be applied to 2 dimensions x/y because we have no control over the z value of ti/to
    const curve = new BezierCurve(
      [prevKeyframe.s[0], prevKeyframe.s[1]],
      [
        prevKeyframe.s[0] + (prevKeyframe.to?.[0] || 0),
        prevKeyframe.s[1] + (prevKeyframe.to?.[1] || 0),
      ],
      [
        prevKeyframe.e[0] + (prevKeyframe.ti?.[0] || 0),
        prevKeyframe.e[1] + (prevKeyframe.ti?.[1] || 0),
      ],
      [prevKeyframe.e[0], prevKeyframe.e[1]],
    );

    const t_tag = getActualPercentComplete(0, prevKeyframe, percentComplete); // if we have spatial interplation we have a single t' (temporal interpolation result)
    const {x, y} = curve.getPointAtTime(t_tag);

    // z value has no spatial interplation so we calculate it linearly
    const z = prevKeyframe.s[2] + (prevKeyframe.e[2] - prevKeyframe.s[2]) * t_tag;

    return toClientValue([x, y, z]);
  }

  return toClientValue(value.k[value.k.length - 2].e);
}

export default getMovieValue;
